// ── directory-collection-state: route/query → useCollection initialState ───
// Parses only keys present in the query string so caller initialState is not
// clobbered (matches packages/ui readUrlState deep-link contract).

import type { CollectionFilterState, CollectionSort, CollectionState } from "@vjal/ui";

const FILTER_QUERY_KEY = "filters";
const SORT_QUERY_KEY = "sort";

export type DirectoryInitialState = Partial<Pick<CollectionState, "q" | "page" | "pageSize" | "sort" | "filters">>;

export function readDirectoryInitialState(routePath?: string, pageSize = 25): DirectoryInitialState {
  const search = routePath?.includes("?") ? routePath.split("?").slice(1).join("?") : "";
  if (!search && typeof window !== "undefined") return readSearchInitialState(window.location.search, pageSize);
  return readSearchInitialState(search ? `?${search}` : "", pageSize);
}

export function readSearchInitialState(search: string, pageSize = 25): DirectoryInitialState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const out: DirectoryInitialState = {};
  if (params.has("q")) out.q = params.get("q") ?? undefined;
  if (params.has("page")) out.page = Math.max(1, Number(params.get("page")) || 1);
  if (params.has("pageSize")) {
    const next = Math.max(1, Number(params.get("pageSize")) || 0);
    if (next) out.pageSize = next;
  }
  if (params.has(SORT_QUERY_KEY)) out.sort = parseSort(params.get(SORT_QUERY_KEY));
  if (params.has(FILTER_QUERY_KEY)) out.filters = parseFilters(params.get(FILTER_QUERY_KEY));
  if (!params.has("pageSize") && pageSize !== 25) out.pageSize = pageSize;
  return out;
}

function parseSort(value: string | null): CollectionSort | null {
  if (!value) return null;
  const [id, dir] = value.split(":");
  if (!id || (dir !== "asc" && dir !== "desc")) return null;
  return { id, dir };
}

function parseFilters(value: string | null): CollectionFilterState {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as CollectionFilterState) : {};
  } catch {
    return {};
  }
}